import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { TApiFetchResponse } from '../base/types';
import GroupCacheService, { isCacheValueInvalid, isStatCacheValueInvalid } from './groupCache';
import GroupService from './groupService';
import { GroupAdminActions } from './slice';
import {
  IGroupInfo,
  IGroupInfoCacheValue,
  IGroupMemberStatistics,
  IGroupMemberStatsCacheValue,
  IGroupTrainingStatisticsApiResponsePayload,
  TTrainingStatisticsResponse,
} from './types';

export default function* groupWatcher() {
  yield all([
    takeEvery(GroupAdminActions.initGroup, initGroupSaga),
    takeEvery(GroupAdminActions.loadGroup, loadGroupSaga),
    takeEvery(GroupAdminActions.loadGroupStats, loadGroupStatsSaga),
  ]);
}

export function* initGroupSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  yield call(GroupService.init, ApiService);
  yield call(GroupCacheService.init, userId);
  yield put(GroupAdminActions.loadGroup());
  yield put(GroupAdminActions.loadGroupStats());
}

export function* loadGroupSaga() {
  let group: IGroupInfo | undefined = yield call(loadGroupFromCache);
  if (!group) {
    group = yield call(loadGroupFromApi);
    yield call(saveGroupToCache, group);
  }
  if (!group) {
    return;
  }
  yield put(GroupAdminActions.updateGroup(group));
}

export function* loadGroupFromCache() {
  try {
    const cacheValue: IGroupInfoCacheValue | undefined = yield call(GroupCacheService.getGroupInfo);
    const isInvalid: boolean = yield call(isCacheValueInvalid, cacheValue);
    if (isInvalid) {
      return;
    }
    return cacheValue?.group;
  } catch {
    return;
  }
}

export function* saveGroupToCache(group: IGroupInfo | undefined) {
  try {
    yield call(GroupCacheService.setGroupInfo, group);
  } catch {
    // do nothing
  }
}

export function* loadGroupFromApi() {
  let group: IGroupInfo | undefined;
  try {
    const response: TApiFetchResponse<typeof GroupService.getGroupInfo> = yield call(GroupService.getGroupInfo);
    group = response.data;
  } catch {
    group = undefined;
  }
  return group;
}

export function* loadGroupStatsSaga() {
  let trainingStats: IGroupTrainingStatisticsApiResponsePayload;
  const cachedStats: IGroupTrainingStatisticsApiResponsePayload | undefined = yield call(loadGroupMemberStatsFromCache);
  if (cachedStats) {
    trainingStats = cachedStats;
  } else {
    trainingStats = yield call(loadGroupMemberStatsFromApi);
  }
  for (const memberId in trainingStats) {
    if (Object.hasOwn(trainingStats, memberId)) {
      const statistics = trainingStats[memberId];
      const memberStats: IGroupMemberStatistics = {
        memberId: Number(memberId),
        statistics,
      };
      yield put(GroupAdminActions.updateGroupStats(memberStats));
    }
  }
}

export function* loadGroupMemberStatsFromCache() {
  try {
    const cacheValue: IGroupMemberStatsCacheValue | undefined = yield call(GroupCacheService.getGroupMemberStats);
    const isInvalid: boolean = yield call(isStatCacheValueInvalid, cacheValue);
    if (isInvalid) {
      return;
    }
    return cacheValue?.stats;
  } catch {
    return;
  }
}

export function* loadGroupMemberStatsFromApi() {
  let trainingStats: IGroupTrainingStatisticsApiResponsePayload;
  try {
    const statisticResponse: TTrainingStatisticsResponse = yield call(GroupService.getGroupStatistic);
    if (!statisticResponse || !statisticResponse.data) {
      trainingStats = {};
    } else {
      trainingStats = statisticResponse.data;
      yield call(saveGroupMemberStatsToCache, trainingStats);
    }
  } catch (error) {
    trainingStats = {};
  }
  return trainingStats;
}

export function* saveGroupMemberStatsToCache(stats: IGroupTrainingStatisticsApiResponsePayload | undefined) {
  try {
    yield call(GroupCacheService.setGroupMemberStats, stats);
  } catch {
    // do nothing if cache not available
  }
}
